<template>
  <!--
    PoolaPoolHero — two distinct bodies for the Poola buy screen (D3), selected by
    `mode`. A CONTENT FRAGMENT mounted inside PoolaPage's <f7-page-content> — never
    its own <f7-page>.

    • mode="ended" → the run-scoped FINALE summary card (this redesign): winner
      congratulation + looping celebration, a profit headline (collective PoolShare
      spent this deal) and a poolcredit-returned row. Data comes from `usePoolaDeal`'s
      `meta.festival`, passed down by PoolaPage via the `festival` prop — the host owns
      the data layer; this stays presentational. It NO LONGER reads /clubs/poolshare-total.
    • mode="empty" → the original collective "Pool" hero: the live club PoolShare total
      from GET /clubs/poolshare-total (usePoolaTeaser) + a localStorage "notify me next
      drop" reminder. UNCHANGED.

    The countdown is intentionally NOT here: empty/ended have no run window to count
    to; the live day/hr/min/sec countdown belongs to PoolaRunHeader (P5).
  -->

  <!-- ── ENDED → run-scoped finale summary card ─────────────────────────────── -->
  <div v-if="mode === 'ended'" class="poola-finale" :data-reduced="reduced">
    <!--
      Looping celebration confetti (canvas). Sits BEHIND the content, clipped to the
      card (overflow:hidden → no horizontal page overflow), and is never started under
      prefers-reduced-motion. Its rAF loop is cancelled on unmount and paused when the
      page/tab is hidden (see startConfetti/stopConfetti + the lifecycle wiring).
    -->
    <canvas ref="confettiCanvas" class="poola-finale__confetti" aria-hidden="true" />

    <div class="poola-finale__body">
      <!-- Kicker + "Poola has ended" flag -->
      <div class="poola-finale__head">
        <span class="poola-finale__kicker">{{ $t('poola.hero.kicker') }}</span>
        <span class="poola-finale__flag">{{ $t('poola.festival.finale.endedFlag') }}</span>
      </div>

      <!-- Winner congratulation -->
      <div class="poola-finale__congrats" role="status">
        <span class="poola-finale__party" aria-hidden="true">🎉</span>
        <p class="poola-finale__congrats-text">{{ $t('poola.festival.finale.congrats') }}</p>
      </div>

      <!-- Profit headline — collective PoolShare spent this deal (amber). -->
      <div class="poola-finale__profit">
        <p class="poola-finale__label">{{ $t('poola.festival.finale.profitLabel') }}</p>
        <!--
          Show the real number only once meta.festival has landed; until then a
          shimmer skeleton, never a misleading hard "0".
        -->
        <div v-if="hasProfit" class="poola-finale__number">
          <span class="poola-finale__amount">{{ profitDisplay }}</span>
          <span class="poola-finale__currency">{{ $t('poola.festival.finale.currency') }}</span>
        </div>
        <div v-else class="poola-finale__skeleton" aria-hidden="true" />
      </div>

      <!--
        Poolcredit returned to members (green gift). Rendered ONLY when the backend
        field `collectivePoolcreditReturnedCents` is PRESENT (!= null) — it is a
        pending BE add (see brief 2026-07-14-poola-finale-collective-poolcredit-returned).
        No 0/placeholder when the field is absent.
      -->
      <div v-if="hasPoolcredit" class="poola-finale__credit">
        <span class="poola-finale__gift" aria-hidden="true">🎁</span>
        <div class="poola-finale__credit-copy">
          <p class="poola-finale__credit-label">{{ $t('poola.festival.finale.creditLabel') }}</p>
          <div class="poola-finale__credit-number">
            <span class="poola-finale__credit-amount">{{ creditDisplay }}</span>
            <span class="poola-finale__credit-currency">{{
              $t('poola.festival.finale.currency')
            }}</span>
          </div>
          <p class="poola-finale__credit-sub">{{ $t('poola.festival.finale.creditSub') }}</p>
        </div>
      </div>

      <p class="poola-finale__caption">{{ $t('poola.festival.finale.caption') }}</p>
    </div>
  </div>

  <!-- ── EMPTY → the original collective "Pool" hero (UNCHANGED) ─────────────── -->
  <div v-else class="poola-hero-fragment">
    <!-- ── Hero ──────────────────────────────────────────────────────────── -->
    <div class="poola-hero">
      <div class="poola-hero__kicker">{{ $t('poola.hero.kicker') }}</div>
      <h1 class="poola-hero__title">{{ heroTitle }}</h1>
      <p class="poola-hero__subtitle">{{ heroBody }}</p>
    </div>

    <!-- ── Collective Pool total card (amber accent) ─────────────────────── -->
    <div class="poola-pool-card">
      <p class="poola-pool-card__header">{{ $t('poola.pool.header') }}</p>
      <!--
        Show the real number only once we have a successful fetch. Until then
        (first load in flight, or a first-load failure the 60s poll retries) we
        render a shimmer skeleton — never a misleading hard "0 EGP".
      -->
      <div v-if="hasLoaded" class="poola-pool-number">
        <span class="poola-pool-number__amount">{{ formattedPool }}</span>
        <span class="poola-pool-number__currency">{{ $t('poola.pool.currency') }}</span>
      </div>
      <div v-else class="poola-pool-skeleton" aria-hidden="true" />
      <p class="poola-pool-card__caption">{{ $t('poola.pool.caption') }}</p>
    </div>

    <!-- ── Notify-me next drop ───────────────────────────────────────────── -->
    <div class="poola-reminder">
      <h3 class="poola-reminder__header">{{ $t('poola.reminder.header') }}</h3>
      <f7-button
        fill
        class="poola-reminder__btn"
        :disabled="reminderDone"
        @click="handleReminder"
      >{{ $t('poola.buy.notify.button') }}</f7-button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePoolaTeaser } from '@/composables/usePoolaTeaser.js';
import { useToast } from '@/composables/useToast.js';
import { prefersReducedMotion } from '@/utils/confetti.js';

const props = defineProps({
  // "empty" → no active run (singleton data:null, or an ineligible/pending
  // member). "ended" → the run closed → the finale summary card.
  mode: {
    type: String,
    default: 'empty',
    validator: (v) => ['empty', 'ended'].includes(v),
  },
  // meta.festival from usePoolaDeal, passed down by PoolaPage on ENDED. Presentational
  // input only — the host owns the fetch. null until the singleton has loaded. The
  // finale card feature-detects each field and never fabricates a number.
  festival: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n({ useScope: 'global' });
const { showSuccess } = useToast();

// ── Western number formatter (never Arabic-Indic) ───────────────────────────
const fmt = new Intl.NumberFormat('en-US');

// ── Finale: profit + poolcredit (cents → whole EGP, matches formattedPool) ──
const profitCents = computed(() => props.festival?.collectivePoolshareUsedCents ?? null);
const creditCents = computed(() => props.festival?.collectivePoolcreditReturnedCents ?? null);
const hasProfit = computed(() => profitCents.value != null);
// Gate the poolcredit row on the BE field being PRESENT. `!= null` hides for both
// undefined and null; a real 0 (field present) still renders.
const hasPoolcredit = computed(() => creditCents.value != null);

/** cents → whole-EGP display string (Western digits, rounded). */
function formatEgp(cents) {
  return fmt.format(Math.round((Number(cents) || 0) / 100));
}

// ── One-shot count-up on the two finale numbers ─────────────────────────────
const reduced = ref(prefersReducedMotion());
const COUNT_UP_MS = 1500;
const animatedProfit = ref(0);
const animatedCredit = ref(0);
const profitDisplay = computed(() => formatEgp(animatedProfit.value));
const creditDisplay = computed(() => formatEgp(animatedCredit.value));

let countUpRaf = null;
let countUpStarted = false;

function easeOutCubic(x) {
  return 1 - (1 - x) ** 3;
}
function nowMs() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}
function stopCountUp() {
  if (countUpRaf != null) {
    cancelAnimationFrame(countUpRaf);
    countUpRaf = null;
  }
}
function runCountUp() {
  if (countUpStarted) return; // once per mount
  if (props.mode !== 'ended' || !hasProfit.value) return; // wait until data lands
  countUpStarted = true;
  const profitTarget = profitCents.value ?? 0;
  const creditTarget = creditCents.value ?? 0;
  // Reduced motion (or no rAF) → land on the final values immediately. Set here in
  // setup (not onMounted) so the FIRST render already shows the numbers — a value
  // set in onMounted only flushes on the next tick, which a synchronous read misses.
  if (reduced.value || typeof requestAnimationFrame !== 'function') {
    animatedProfit.value = profitTarget;
    animatedCredit.value = creditTarget;
    return;
  }
  const start = nowMs();
  const step = () => {
    const p = Math.min(1, (nowMs() - start) / COUNT_UP_MS);
    const e = easeOutCubic(p);
    animatedProfit.value = profitTarget * e;
    animatedCredit.value = creditTarget * e;
    if (p < 1) {
      countUpRaf = requestAnimationFrame(step);
    } else {
      animatedProfit.value = profitTarget;
      animatedCredit.value = creditTarget;
      countUpRaf = null;
    }
  };
  countUpRaf = requestAnimationFrame(step);
}

// Kick the count-up off synchronously when the data is already present (the common
// ENDED case — festival is loaded before this fragment mounts); the watch below
// covers a late-arriving festival. Running here rather than in onMounted keeps the
// reduced-motion first paint correct.
runCountUp();

// ── Looping confetti (canvas) ───────────────────────────────────────────────
// A continuous, composite-cheap fall of Poola-palette flakes clipped to the card.
// Kept OUT of the shared one-shot util (confetti.js) because that helper self-removes
// after a single burst; here we own the rAF loop so we can pause/resume/cancel it.
const CONFETTI_COLORS = ['#fbbf24', '#a78bfa', '#f472b6', '#34d399', '#60a5fa', '#ffffff'];
const CONFETTI_COUNT = 26;
const confettiCanvas = ref(null);
let confettiCtx = null;
let confettiRaf = null;
let confettiParticles = [];
let confettiW = 0;
let confettiH = 0;
let confettiLastTs = 0;
let resizeObserver = null;
let pageEl = null;

function seedParticles() {
  confettiParticles = Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
    x: Math.random() * confettiW,
    y: Math.random() * confettiH - confettiH, // pre-spread above the card
    size: 5 + Math.random() * 5,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    vx: (Math.random() - 0.5) * 0.7, // gentle horizontal drift (px / 16ms)
    vy: 0.8 + Math.random() * 1.2, // fall speed (px / 16ms)
    rot: Math.random() * Math.PI * 2,
    vr: (Math.random() - 0.5) * 0.22,
  }));
}

function sizeConfetti() {
  const canvas = confettiCanvas.value;
  if (!canvas) return;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  if (!w || !h) return;
  const dpr = Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2);
  canvas.width = Math.floor(w * dpr);
  canvas.height = Math.floor(h * dpr);
  confettiW = w;
  confettiH = h;
  if (confettiCtx) confettiCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  if (!confettiParticles.length) seedParticles();
}

function drawConfetti(ts) {
  if (confettiCtx == null) return;
  const dt = confettiLastTs ? Math.min(50, ts - confettiLastTs) : 16;
  confettiLastTs = ts;
  const k = dt / 16; // normalise to 60fps steps
  const ctx = confettiCtx;
  ctx.clearRect(0, 0, confettiW, confettiH);
  for (const pt of confettiParticles) {
    pt.x += pt.vx * k;
    pt.y += pt.vy * k;
    pt.rot += pt.vr * k;
    if (pt.y - pt.size > confettiH) {
      // recycle from the top for a seamless loop
      pt.y = -pt.size;
      pt.x = Math.random() * confettiW;
    }
    if (pt.x < -pt.size) pt.x = confettiW + pt.size;
    else if (pt.x > confettiW + pt.size) pt.x = -pt.size;
    ctx.save();
    ctx.translate(pt.x, pt.y);
    ctx.rotate(pt.rot);
    ctx.fillStyle = pt.color;
    ctx.fillRect(-pt.size / 2, -pt.size * 0.3, pt.size, pt.size * 0.6);
    ctx.restore();
  }
  confettiRaf = requestAnimationFrame(drawConfetti);
}

function startConfetti() {
  if (reduced.value) return; // reduced motion → no canvas animation at all
  if (confettiRaf != null) return; // already running
  const canvas = confettiCanvas.value;
  if (!canvas || typeof canvas.getContext !== 'function') return;
  confettiCtx = canvas.getContext('2d');
  if (!confettiCtx) return; // happy-dom / no 2d context → no-op (keeps tests safe)
  sizeConfetti();
  if (!confettiW || !confettiH) return;
  confettiLastTs = 0;
  confettiRaf = requestAnimationFrame(drawConfetti);
}

function stopConfetti() {
  if (confettiRaf != null) {
    cancelAnimationFrame(confettiRaf);
    confettiRaf = null;
  }
}

// ── Pause/resume drivers (battery: never animate a backgrounded card) ────────
function resumeCelebration() {
  if (props.mode !== 'ended') return;
  if (typeof document !== 'undefined' && document.hidden) return;
  startConfetti();
}
function onVisibility() {
  if (typeof document !== 'undefined' && document.hidden) stopConfetti();
  else resumeCelebration();
}
function onPageBeforeOut() {
  stopConfetti();
}
function onPageBeforeIn() {
  resumeCelebration();
}

// Run the count-up as soon as festival data is available (it may land after mount).
watch(hasProfit, (ready) => {
  if (ready) runCountUp();
});

onMounted(() => {
  if (props.mode !== 'ended') return;
  // Count-up already kicked off synchronously in setup (+ a watch for late data).
  // Start confetti after layout so the canvas has real dimensions.
  nextTick(() => {
    if (typeof document === 'undefined' || !document.hidden) startConfetti();
  });
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
  }
  // F7 dispatches page:beforeout/page:beforein as DOM events on the enclosing
  // .page element (verified in framework7-bundle.js). This fragment is not its own
  // <f7-page>, so we hook the ancestor page directly to pause on navigate-away.
  pageEl = confettiCanvas.value?.closest?.('.page') ?? null;
  if (pageEl) {
    pageEl.addEventListener('page:beforeout', onPageBeforeOut);
    pageEl.addEventListener('page:beforein', onPageBeforeIn);
  }
  if (typeof ResizeObserver !== 'undefined' && confettiCanvas.value) {
    resizeObserver = new ResizeObserver(() => {
      // Re-fit while running; otherwise (re)start — this also recovers the case
      // where the very first startConfetti() bailed because the card had no
      // laid-out height yet (0×0), so confetti still begins once it gets one.
      if (confettiRaf != null) sizeConfetti();
      else resumeCelebration();
    });
    resizeObserver.observe(confettiCanvas.value);
  }
});

onBeforeUnmount(() => {
  stopConfetti();
  stopCountUp();
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibility);
  }
  if (pageEl) {
    pageEl.removeEventListener('page:beforeout', onPageBeforeOut);
    pageEl.removeEventListener('page:beforein', onPageBeforeIn);
    pageEl = null;
  }
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});

// ── EMPTY-mode data layer (only wired for empty — the ended card dropped the
// /clubs/poolshare-total teaser; instantiating it here would fire a needless
// request + poll). Safe to call conditionally: `mode` is fixed for a mounted
// instance (PoolaPage remounts a fresh child per state), so this runs once during
// setup and its onMounted/onUnmounted register correctly. Reading `mode` once (no
// reactivity) is deliberate here — the host remounts a fresh instance per state.
// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- intentional one-shot read; see note above
const teaser = props.mode === 'empty' ? usePoolaTeaser() : null;
const totalEgp = teaser?.totalEgp ?? ref(0);
const hasLoaded = teaser?.hasLoaded ?? ref(false);
const formattedPool = computed(() => fmt.format(Math.round(totalEgp.value)));

// ── Empty-mode hero copy (reuses the P0-B buy keys) ─────────────────────────
const heroTitle = computed(() => t('poola.buy.empty.header'));
const heroBody = computed(() => t('poola.buy.empty.body'));

// ── Notify-me reminder (localStorage; cosmetic in v1 — FE plan Section 10.1) ───────
const REMINDER_STORAGE_KEY = 'poola:reminder-set';

function _readReminderDone() {
  try {
    return localStorage.getItem(REMINDER_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

const reminderDone = ref(_readReminderDone());

function handleReminder() {
  if (reminderDone.value) return;
  try {
    localStorage.setItem(REMINDER_STORAGE_KEY, '1');
  } catch {
    /* storage blocked — still show the toast; flag is best-effort */
  }
  reminderDone.value = true;
  showSuccess(t('poola.buy.notify.toast'), { position: 'top', closeTimeout: 4500 });
}
</script>

<style scoped>
/* ══ ENDED — finale summary card ═══════════════════════════════════════════ */
.poola-finale {
  position: relative;
  isolation: isolate;
  overflow: hidden; /* clips the confetti canvas → no horizontal page overflow */
  margin: 20px 16px 40px;
  background: var(--poolit-bg-primary);
  border-radius: var(--poolit-radius-lg);
  box-shadow: var(--poolit-shadow-soft);
  border: 1px solid var(--poolit-border-color);
  border-top: 3px solid var(--poolit-warning-500);
}

.poola-finale__confetti {
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.poola-finale__body {
  position: relative;
  z-index: 1;
  padding: 24px 20px 22px;
  text-align: center;
}

/* ── Head: kicker + ended flag ───────────────────────────────────────────── */
.poola-finale__head {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.poola-finale__kicker {
  display: inline-block;
  background: var(--poolit-primary-100);
  color: var(--poolit-primary-700);
  border-radius: var(--poolit-radius-full);
  padding: 4px 12px;
  font-size: var(--poolit-font-size-caption);
  font-weight: var(--poolit-font-weight-semibold);
  letter-spacing: 0.02em;
}

.poola-finale__flag {
  display: inline-block;
  background: var(--poolit-warning-100);
  color: var(--poolit-warning-700);
  border-radius: var(--poolit-radius-full);
  padding: 4px 12px;
  font-size: var(--poolit-font-size-caption);
  font-weight: var(--poolit-font-weight-bold);
}

/* ── Winner congratulation ───────────────────────────────────────────────── */
.poola-finale__congrats {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-bottom: 18px;
}

.poola-finale__party {
  font-size: 2.4rem;
  line-height: 1;
  display: inline-block;
  animation: poola-finale-wiggle 1.8s ease-in-out infinite;
  will-change: transform;
}

.poola-finale__congrats-text {
  margin: 0;
  color: var(--poolit-primary-800);
  font-size: 1.0625rem;
  font-weight: var(--poolit-font-weight-bold);
  line-height: var(--poolit-line-height-body);
}

/* ── Profit headline (amber) ─────────────────────────────────────────────── */
.poola-finale__profit {
  margin-bottom: 18px;
}

.poola-finale__label {
  margin: 0 0 8px;
  color: var(--poolit-text-secondary);
  font-size: var(--poolit-font-size-caption);
  font-weight: var(--poolit-font-weight-medium);
}

.poola-finale__number {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}

.poola-finale__amount {
  font-size: 2.75rem;
  font-weight: var(--poolit-font-weight-bold);
  color: var(--poolit-warning-600);
  line-height: 1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

.poola-finale__currency {
  font-size: 1.1rem;
  font-weight: var(--poolit-font-weight-semibold);
  color: var(--poolit-warning-500);
}

/* ── Poolcredit returned (green gift) ────────────────────────────────────── */
.poola-finale__credit {
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: start;
  margin: 0 0 18px;
  padding: 14px 16px;
  border-radius: var(--poolit-radius-md);
  background: var(--poolit-success-50);
  border: 1px solid var(--poolit-success-100);
}

.poola-finale__gift {
  flex-shrink: 0;
  font-size: 1.9rem;
  line-height: 1;
  animation: poola-finale-bounce 1.6s ease-in-out infinite;
  will-change: transform;
}

.poola-finale__credit-copy {
  flex: 1;
  min-width: 0;
}

.poola-finale__credit-label {
  margin: 0 0 2px;
  color: var(--poolit-success-dark);
  font-size: var(--poolit-font-size-small);
  font-weight: var(--poolit-font-weight-semibold);
}

.poola-finale__credit-number {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
}

.poola-finale__credit-amount {
  font-size: 1.5rem;
  font-weight: var(--poolit-font-weight-bold);
  color: var(--poolit-success-600);
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.poola-finale__credit-currency {
  font-size: 0.85rem;
  font-weight: var(--poolit-font-weight-semibold);
  color: var(--poolit-success-600);
}

.poola-finale__credit-sub {
  margin: 2px 0 0;
  color: var(--poolit-text-muted);
  font-size: var(--poolit-font-size-small);
}

/* ── Caption ─────────────────────────────────────────────────────────────── */
.poola-finale__caption {
  margin: 0;
  color: var(--poolit-text-muted);
  font-size: var(--poolit-font-size-small);
  line-height: var(--poolit-line-height-body);
  max-width: 320px;
  margin-inline: auto;
}

/* Shimmer placeholder shown until meta.festival lands. */
.poola-finale__skeleton {
  height: 2.75rem;
  width: 160px;
  max-width: 70%;
  margin: 0 auto;
  border-radius: var(--poolit-radius-md);
  background: linear-gradient(
    90deg,
    var(--poolit-bg-soft) 25%,
    var(--poolit-border-color) 37%,
    var(--poolit-bg-soft) 63%
  );
  background-size: 400% 100%;
  animation: poola-shimmer 1.4s ease infinite;
}

@keyframes poola-finale-wiggle {
  0%,
  100% {
    transform: rotate(-8deg);
  }

  50% {
    transform: rotate(8deg);
  }
}

@keyframes poola-finale-bounce {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-6px);
  }
}

/* Reduced motion → freeze the emoji flourishes (confetti + count-up are JS-gated). */
.poola-finale[data-reduced='true'] .poola-finale__party,
.poola-finale[data-reduced='true'] .poola-finale__gift {
  animation: none;
}

@media (prefers-reduced-motion: reduce) {
  .poola-finale__party,
  .poola-finale__gift {
    animation: none;
  }
}

/* ══ EMPTY — original collective "pool" hero (unchanged) ═══════════════════ */
.poola-hero {
  padding: 28px 20px 16px;
  text-align: center;
}

.poola-hero__kicker {
  display: inline-block;
  background: var(--poolit-primary-100);
  color: var(--poolit-primary-700);
  border-radius: var(--poolit-radius-full);
  padding: 4px 14px;
  font-size: var(--poolit-font-size-caption);
  font-weight: var(--poolit-font-weight-semibold);
  margin-bottom: 12px;
  letter-spacing: 0.02em;
}

.poola-hero__title {
  color: var(--poolit-primary-800);
  font-size: var(--poolit-font-size-h2);
  font-weight: var(--poolit-font-weight-bold);
  margin: 0 0 10px;
  line-height: var(--poolit-line-height-h2);
}

.poola-hero__subtitle {
  color: var(--poolit-text-muted);
  font-size: var(--poolit-font-size-body);
  margin: 0;
  line-height: var(--poolit-line-height-body);
  max-width: 320px;
  margin-inline: auto;
}

/* ── Pool total card (white, amber top-border accent) ────────────────────── */
.poola-pool-card {
  margin: 0 16px 16px;
  padding: 20px;
  background: var(--poolit-bg-primary);
  border-radius: var(--poolit-radius-lg);
  box-shadow: var(--poolit-shadow-soft);
  border: 1px solid var(--poolit-border-color);
  border-top: 3px solid var(--poolit-warning-500);
  text-align: center;
}

.poola-pool-card__header {
  color: var(--poolit-text-secondary);
  font-size: var(--poolit-font-size-caption);
  font-weight: var(--poolit-font-weight-medium);
  margin: 0 0 12px;
}

.poola-pool-number {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 10px;
}

.poola-pool-number__amount {
  font-size: 2.75rem;
  font-weight: var(--poolit-font-weight-bold);
  color: var(--poolit-warning-600);
  line-height: 1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

.poola-pool-number__currency {
  font-size: 1.1rem;
  font-weight: var(--poolit-font-weight-semibold);
  color: var(--poolit-warning-500);
}

.poola-pool-card__caption {
  color: var(--poolit-text-muted);
  font-size: var(--poolit-font-size-small);
  margin: 0;
}

/* Shimmer placeholder shown until the first successful club-total fetch. */
.poola-pool-skeleton {
  height: 2.75rem;
  width: 160px;
  max-width: 70%;
  margin: 0 auto 10px;
  border-radius: var(--poolit-radius-md);
  background: linear-gradient(
    90deg,
    var(--poolit-bg-soft) 25%,
    var(--poolit-border-color) 37%,
    var(--poolit-bg-soft) 63%
  );
  background-size: 400% 100%;
  animation: poola-shimmer 1.4s ease infinite;
}

@keyframes poola-shimmer {
  0% {
    background-position: 100% 0;
  }

  100% {
    background-position: 0 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .poola-pool-skeleton,
  .poola-finale__skeleton {
    animation: none;
  }
}

/* ── Reminder ────────────────────────────────────────────────────────────── */
.poola-reminder {
  margin: 0 16px 40px;
  padding: 20px;
  background: var(--poolit-bg-primary);
  border-radius: var(--poolit-radius-lg);
  box-shadow: var(--poolit-shadow-soft);
  border: 1px solid var(--poolit-border-color);
  text-align: center;
}

.poola-reminder__header {
  color: var(--poolit-text-primary);
  font-size: var(--poolit-font-size-body);
  font-weight: var(--poolit-font-weight-semibold);
  margin: 0 0 16px;
}

.poola-reminder__btn {
  --f7-button-fill-bg-color: var(--poolit-primary-600);
  --f7-button-fill-text-color: #fff;
  --f7-button-fill-hover-bg-color: var(--poolit-primary-700);
  width: 100%;
}
</style>

<!-- Non-scoped block: rules that must reach [dir='rtl'] on <html>.
     Per CLAUDE.md: never :global(ancestor) .descendant inside <style scoped>. -->
<style>
[dir='rtl'] .poola-pool-number,
[dir='rtl'] .poola-finale__number,
[dir='rtl'] .poola-finale__credit-number {
  flex-direction: row-reverse;
}
</style>
